import { useEffect, useState, type ReactElement } from "react";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EditIcon from "@mui/icons-material/Edit";
import EditCalendarIcon from "@mui/icons-material/EditCalendar";
import EditLocationAltIcon from "@mui/icons-material/EditLocationAlt";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import LocalPoliceIcon from "@mui/icons-material/LocalPolice";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ShareIcon from "@mui/icons-material/Share";
import StopCircleIcon from "@mui/icons-material/StopCircle";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import TableChartIcon from "@mui/icons-material/TableChart";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import type { Promisable } from "type-fest";

import { dayjs } from "@thalamos/common";
import type { AuditTrailEntryUnbranded } from "@thalamos/vision-common";
import { tk } from "@thalamos/vision-common";

import { TypographyI18N } from "../../components/TypographyI18N/TypographyI18N.js";
import { useToastNotifications } from "../../hooks/useToastNotifications.js";
import {
  copyActivityLogToClipboard,
  downloadActivityLogCsv,
  downloadActivityLogPdf,
  extractActivityLogRows,
} from "./helpers/exportActivityLog.js";
import { getActivityLogMessage } from "./helpers/getActivityLogMessage.js";
import { getEntryDetailRows } from "./helpers/getEntryDetailRows.js";

// ─── Constants ─────────────────────────────────────────────────────────────

const NHS_GREEN = "#007F3B";
const NHS_GREEN_BG = "rgba(0, 127, 59, 0.05)";
const NHS_GREEN_ICON_BG = "rgba(0, 127, 59, 0.12)";
const POLICE_BLUE_BG = "rgba(25, 118, 210, 0.05)";

// ─── Type helpers ──────────────────────────────────────────────────────────

const NHS_ENTRY_TYPES = new Set([
  "aspireReviewIncident",
  "aspireAcceptIncident",
  "aspireRejectIncident",
  "aspireRequestPolicePresence",
  "aspireNotRequestPolicePresence",
]);

function isNhsEntry(entry: AuditTrailEntryUnbranded): boolean {
  return NHS_ENTRY_TYPES.has(entry.type);
}

// ─── Icon mapping ──────────────────────────────────────────────────────────

function getEntryIcon(type: string): ReactElement {
  switch (type) {
    case "createIncident":
      return <PlayArrowIcon fontSize="small" />;
    case "updateIncidentData":
      return <EditIcon fontSize="small" />;
    case "formShared":
      return <ShareIcon fontSize="small" />;
    case "endIncident":
    case "preEndIncident":
    case "preTriageEndIncident":
    case "finishForm":
      return <StopCircleIcon fontSize="small" />;
    case "transferIncident":
    case "resolveTransfer":
    case "revokeTransfer":
      return <SwapHorizIcon fontSize="small" />;
    case "officerRecordRequestPolicePresence":
      return <LocalPoliceIcon fontSize="small" />;
    case "aspireReviewIncident":
    case "aspireAcceptIncident":
    case "aspireRejectIncident":
      return <LocalHospitalIcon fontSize="small" />;
    case "aspireRequestPolicePresence":
    case "aspireNotRequestPolicePresence":
      return <LocalPoliceIcon fontSize="small" />;
    case "createPlaceOfSafety":
      return <AddCircleOutlineIcon fontSize="small" />;
    case "adminEditReportingFields":
      return <EditCalendarIcon fontSize="small" />;
    case "adminRelinkPlaceOfSafety":
      return <EditLocationAltIcon fontSize="small" />;
    default:
      return <CheckCircleIcon fontSize="small" />;
  }
}

// ─── Single timeline entry ─────────────────────────────────────────────────

function TimelineEntry({
  entry,
  index,
  isDesktop,
  isLastEntry,
  isOpen,
  onClickEntry,
}: {
  entry: AuditTrailEntryUnbranded;
  index: number;
  isDesktop: boolean;
  isLastEntry: boolean;
  isOpen: boolean;
  onClickEntry: (entry: AuditTrailEntryUnbranded) => void;
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(
      () => {
        setVisible(true);
      },
      Math.min(index * 60, 600),
    );
    return () => {
      clearTimeout(timer);
    };
  }, [index]);

  const nhsEntry = isNhsEntry(entry);
  const message = getActivityLogMessage(entry);
  const side: "left" | "right" = isDesktop && nhsEntry ? "right" : "left";
  const formattedTime = dayjs
    .utc(entry.timestamp)
    .tz("Europe/London")
    .format("DD/MM/YYYY HH:mm");

  const accentColour = nhsEntry ? NHS_GREEN : theme.palette.primary.main;
  const showPulse = isLastEntry && isOpen;

  const entryCard = (
    <Card
      elevation={0}
      sx={{
        border: `1px solid ${theme.palette.divider}`,
        borderLeft: `3px solid ${accentColour}`,
        borderRadius: 2,
        bgcolor: nhsEntry ? NHS_GREEN_BG : POLICE_BLUE_BG,
        transition: "box-shadow 0.2s",
        "&:hover": { boxShadow: 2, cursor: "pointer" },
      }}
      data-testid={`timeline-entry-${entry.type}`}
      onClick={() => {
        onClickEntry(entry);
      }}
    >
      <CardContent
        sx={{
          p: 2,
          "&:last-child": { pb: 2 },
          display: "flex",
          alignItems: "flex-start",
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: "50%",
            bgcolor: nhsEntry ? NHS_GREEN_ICON_BG : "primary.50",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: accentColour,
            flexShrink: 0,
            mt: 0.25,
          }}
        >
          {getEntryIcon(entry.type)}
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 0.5,
            }}
          >
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, lineHeight: 1.4 }}
            >
              {t(message.key, message.options)}
            </Typography>
            {nhsEntry && (
              <Chip
                label="NHS"
                size="small"
                data-testid="nhs-chip"
                sx={{
                  bgcolor: NHS_GREEN,
                  color: "white",
                  fontWeight: 700,
                  fontSize: "0.625rem",
                  height: 20,
                }}
              />
            )}
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mt: 0.5,
              flexWrap: "wrap",
            }}
          >
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {formattedTime}
            </Typography>
            {entry.userName && (
              <>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  ·
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: "text.secondary", fontWeight: 500 }}
                >
                  {entry.userName}
                </Typography>
              </>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  // ── Desktop dual-sided layout ──
  if (isDesktop) {
    return (
      <Collapse in={visible} timeout={400}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 40px 1fr",
            gap: 0,
            alignItems: "stretch",
          }}
        >
          {/* Left side */}
          <Box
            sx={{
              pr: 2,
              pb: 2,
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "flex-start",
            }}
          >
            {side === "left" ? entryCard : null}
          </Box>

          {/* Centre line + dot */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: "50%",
                bgcolor: showPulse ? "#22c55e" : theme.palette.primary.main,
                border: `2px solid ${theme.palette.background.paper}`,
                boxShadow: `0 0 0 2px ${showPulse ? "#22c55e" : theme.palette.primary.main}`,
                zIndex: 1,
                mt: "10px",
                flexShrink: 0,
                ...(showPulse
                  ? {
                      "@keyframes pulse-open": {
                        "0%": {
                          boxShadow:
                            "0 0 0 2px #22c55e, 0 0 0 0px rgba(34, 197, 94, 0.5)",
                        },
                        "70%": {
                          boxShadow:
                            "0 0 0 2px #22c55e, 0 0 0 8px rgba(34, 197, 94, 0)",
                        },
                        "100%": {
                          boxShadow:
                            "0 0 0 2px #22c55e, 0 0 0 0px rgba(34, 197, 94, 0)",
                        },
                      },
                      animation: "pulse-open 2s ease-in-out infinite",
                    }
                  : {}),
              }}
            />
            {!isLastEntry && (
              <Box
                sx={{
                  width: 2,
                  flex: 1,
                  bgcolor: theme.palette.divider,
                  mt: 0.5,
                }}
              />
            )}
          </Box>

          {/* Right side */}
          <Box
            sx={{
              pl: 2,
              pb: 2,
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "flex-start",
            }}
          >
            {side === "right" ? entryCard : null}
          </Box>
        </Box>
      </Collapse>
    );
  }

  // ── Mobile single-sided layout ──
  return (
    <Collapse in={visible} timeout={400}>
      <Box
        sx={{
          display: "flex",
          gap: 2,
        }}
      >
        {/* Line + dot */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: 24,
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              bgcolor: showPulse ? "#22c55e" : theme.palette.primary.main,
              border: `2px solid ${theme.palette.background.paper}`,
              boxShadow: `0 0 0 2px ${showPulse ? "#22c55e" : theme.palette.primary.main}`,
              zIndex: 1,
              mt: "12px",
              flexShrink: 0,
              ...(showPulse
                ? {
                    "@keyframes pulse-open": {
                      "0%": {
                        boxShadow:
                          "0 0 0 2px #22c55e, 0 0 0 0px rgba(34, 197, 94, 0.5)",
                      },
                      "70%": {
                        boxShadow:
                          "0 0 0 2px #22c55e, 0 0 0 8px rgba(34, 197, 94, 0)",
                      },
                      "100%": {
                        boxShadow:
                          "0 0 0 2px #22c55e, 0 0 0 0px rgba(34, 197, 94, 0)",
                      },
                    },
                    animation: "pulse-open 2s ease-in-out infinite",
                  }
                : {}),
            }}
          />
          {!isLastEntry && (
            <Box
              sx={{
                width: 2,
                flex: 1,
                bgcolor: theme.palette.divider,
                mt: 0.5,
              }}
            />
          )}
        </Box>

        {/* Entry card */}
        <Box sx={{ flex: 1, minWidth: 0, pb: 2 }}>{entryCard}</Box>
      </Box>
    </Collapse>
  );
}

// ─── Entry detail dialog ───────────────────────────────────────────────────

function EntryDetailDialog({
  entry,
  onClose,
}: {
  entry: AuditTrailEntryUnbranded | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();

  if (!entry) return null;

  const formattedTime = dayjs
    .utc(entry.timestamp)
    .tz("Europe/London")
    .format("DD/MM/YYYY HH:mm:ss");

  const message = getActivityLogMessage(entry);
  const detailRows = getEntryDetailRows(entry, t);

  return (
    <Dialog
      open={!!entry}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      data-testid="entry-detail-dialog"
    >
      <DialogTitle sx={{ fontWeight: 700 }}>
        {t(tk("incidentSummary.entryDetail"))}
      </DialogTitle>
      <DialogContent dividers>
        <Stack sx={{ gap: 1.5 }}>
          <DetailPair
            label={t(tk("incidentSummary.entryDetailEvent"))}
            value={t(message.key, message.options)}
          />
          <DetailPair
            label={t(tk("incidentSummary.entryDetailTimestamp"))}
            value={formattedTime}
          />
          {entry.userName && (
            <DetailPair
              label={t(tk("incidentSummary.entryDetailUser"))}
              value={entry.userName}
            />
          )}
          {entry.organisationName && (
            <DetailPair
              label={t(tk("incidentSummary.entryDetailOrganisation"))}
              value={entry.organisationName}
            />
          )}
          {detailRows.length > 0 && (
            <>
              <Divider sx={{ my: 0.5 }} />
              <Typography
                variant="caption"
                sx={{ fontWeight: 700, color: "text.secondary" }}
              >
                {t(tk("incidentSummary.entryDetailData"))}
              </Typography>
              {detailRows.map((row, index) => (
                <DetailPair
                  key={`${row.label}-${String(index)}`}
                  label={row.label}
                  value={row.value}
                />
              ))}
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t(tk("incidentSummary.close"))}</Button>
      </DialogActions>
    </Dialog>
  );
}

function DetailPair({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Typography variant="caption" sx={{ color: "text.secondary" }}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          fontWeight: 500,
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

// ─── Export menu ────────────────────────────────────────────────────────────

function ExportMenu({
  getAllEntries,
  getMessage,
}: {
  getAllEntries: () => Promisable<AuditTrailEntryUnbranded[]>;
  getMessage: (entry: AuditTrailEntryUnbranded) => string;
}) {
  const { t } = useTranslation();
  const notifications = useToastNotifications();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleExport = async (format: "clipboard" | "csv" | "pdf") => {
    setAnchorEl(null);
    let entries: AuditTrailEntryUnbranded[];
    try {
      entries = await getAllEntries();
    } catch {
      notifications.show(t(tk("incidentSummary.exportFailed")), {
        severity: "error",
      });
      return;
    }
    const rows = extractActivityLogRows(entries, getMessage);
    const title = t(tk("incidentSummary.exportTitle"));

    switch (format) {
      case "clipboard": {
        const success = await copyActivityLogToClipboard(rows);
        notifications.show(
          t(
            tk(
              success
                ? "incidentSummary.exportCopied"
                : "incidentSummary.exportCopyFailed",
            ),
          ),
          { severity: success ? "success" : "error" },
        );
        break;
      }
      case "csv":
        downloadActivityLogCsv(rows, title);
        break;
      case "pdf":
        downloadActivityLogPdf(rows, title);
        break;
    }
  };

  return (
    <>
      <IconButton
        size="small"
        onClick={(e) => {
          setAnchorEl(e.currentTarget);
        }}
        aria-label={t(tk("incidentSummary.exportMenu"))}
        data-testid="export-menu-btn"
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => {
          setAnchorEl(null);
        }}
      >
        <MenuItem
          onClick={() => {
            void handleExport("clipboard");
          }}
        >
          <ListItemIcon>
            <ContentCopyIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>
            {t(tk("incidentSummary.exportClipboard"))}
          </ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            void handleExport("csv");
          }}
        >
          <ListItemIcon>
            <TableChartIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t(tk("incidentSummary.exportCsv"))}</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            void handleExport("pdf");
          }}
        >
          <ListItemIcon>
            <PictureAsPdfIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t(tk("incidentSummary.exportPdf"))}</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
}

// ─── Timeline component ────────────────────────────────────────────────────

interface IncidentTimelineProps {
  entries: AuditTrailEntryUnbranded[];
  totalCount: number;
  /** Every entry on the incident, including those not yet loaded into the timeline. */
  getAllEntries: () => Promisable<AuditTrailEntryUnbranded[]>;
  onLoadMore: () => void;
  hasMore: boolean;
  loadingMore: boolean;
  isOpen?: boolean;
}

export function IncidentTimeline({
  entries,
  totalCount,
  getAllEntries,
  onLoadMore,
  hasMore,
  loadingMore,
  isOpen = false,
}: IncidentTimelineProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const [selectedEntry, setSelectedEntry] =
    useState<AuditTrailEntryUnbranded | null>(null);

  const getTranslatedMessage = (entry: AuditTrailEntryUnbranded): string => {
    const msg = getActivityLogMessage(entry);
    return t(msg.key, msg.options);
  };

  return (
    <Stack data-testid="incident-timeline" sx={{ height: "100%" }}>
      {/* Header */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 2,
          bgcolor: "background.default",
          pb: 1,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <TypographyI18N
              variant="subtitle1"
              sx={{ fontWeight: 700 }}
              translationKey={tk("incidentSummary.activityTimeline")}
            />
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {t(tk("incidentSummary.eventCount"), { count: totalCount })}
            </Typography>
          </Box>
          {entries.length > 0 && (
            <ExportMenu
              getAllEntries={getAllEntries}
              getMessage={getTranslatedMessage}
            />
          )}
        </Box>

        <Divider />
      </Box>

      {/* Entries */}
      <Box sx={{ pt: 2 }}>
        {entries.length === 0 ? (
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", textAlign: "center", py: 4 }}
          >
            {t(tk("incidentSummary.noActivityYet"))}
          </Typography>
        ) : (
          entries.map((entry, idx) => (
            <TimelineEntry
              key={entry.id}
              entry={entry}
              index={idx}
              isDesktop={isDesktop}
              isLastEntry={idx === entries.length - 1}
              isOpen={isOpen}
              onClickEntry={setSelectedEntry}
            />
          ))
        )}
      </Box>

      {/* Load more */}
      {hasMore && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
          <Button
            onClick={onLoadMore}
            disabled={loadingMore}
            variant="text"
            data-testid="load-more-btn"
          >
            {loadingMore ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
            {t(tk("incidentSummary.loadMore"))}
          </Button>
        </Box>
      )}

      {/* Detail popout */}
      <EntryDetailDialog
        entry={selectedEntry}
        onClose={() => {
          setSelectedEntry(null);
        }}
      />
    </Stack>
  );
}
